/**
 * Lightweight Mapbox forward-geocoding with localStorage caching, used by the
 * "Find Buyers for a Property" panel to place a property and the suburbs buyers
 * have marked on the map so the surrounding-suburb radius can be computed.
 *
 * The CRM backend has no geocoding token, but the frontend already carries a
 * Mapbox token for the map views, so geocoding is done here. Suburb coordinates
 * don't move, so results are cached in localStorage indefinitely (including
 * "not found" so we don't refetch the same miss every time).
 */

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
const CACHE_PREFIX = 'fb_geo:v1:';

export const hasGeocoder = () => Boolean(MAPBOX_TOKEN);

const readCache = (key) => {
    try {
        const raw = localStorage.getItem(CACHE_PREFIX + key);
        if (!raw) return undefined; // never looked up
        return JSON.parse(raw); // { lat, lng } | null (known miss)
    } catch {
        return undefined;
    }
};

const writeCache = (key, value) => {
    try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value)); } catch { /* quota / private mode */ }
};

/**
 * Forward-geocode a free-text query to { lat, lng } | null. Restricted to
 * Australia. Returns null when nothing is found or no token is configured.
 *
 * Pass `proximity` ({lat,lng}) when geocoding an ambiguous suburb NAME so it
 * resolves to the local instance: without it, "Brunswick"/"Glenroy" match a
 * same-named town on the far side of the country. We bias by proximity AND clamp
 * with a ~40km bbox around it, which was needed to fix e.g. Glenroy near Fawkner.
 */
export const geocodePlace = async (query, { types = 'address,place,locality,postcode', proximity = null } = {}) => {
    const q = (query || '').trim();
    if (!q || !MAPBOX_TOKEN) return null;
    const proxKey = proximity ? `@${proximity.lat.toFixed(1)},${proximity.lng.toFixed(1)}` : '';
    const key = `${types}|${q.toLowerCase()}${proxKey}`;
    const cached = readCache(key);
    if (cached !== undefined) return cached;
    try {
        let url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json`
            + `?access_token=${MAPBOX_TOKEN}&country=au&limit=1&types=${encodeURIComponent(types)}`;
        if (proximity) {
            url += `&proximity=${proximity.lng},${proximity.lat}`;
            const dLat = 40 / 111;
            const dLng = 40 / (111 * Math.cos((proximity.lat * Math.PI) / 180));
            url += `&bbox=${proximity.lng - dLng},${proximity.lat - dLat},${proximity.lng + dLng},${proximity.lat + dLat}`;
        }
        const res = await fetch(url);
        if (!res.ok) { writeCache(key, null); return null; }
        const data = await res.json();
        const center = data?.features?.[0]?.center; // [lng, lat]
        if (Array.isArray(center) && center.length === 2) {
            const coords = { lat: Number(center[1]), lng: Number(center[0]) };
            writeCache(key, coords);
            return coords;
        }
        writeCache(key, null);
        return null;
    } catch {
        return null;
    }
};

/**
 * Geocode a suburb (optionally with state) to a centroid { lat, lng } | null.
 * Pass `origin` so ambiguous suburb names bias to the local instance.
 */
export const geocodeSuburb = (suburb, state, origin = null) => {
    const q = [suburb, state, 'Australia'].filter(Boolean).join(', ');
    return geocodePlace(q, { types: 'locality,place', proximity: origin });
};

/** Great-circle distance in kilometres. */
export const haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
};

/**
 * Enrich the backend's marked-suburb rows with Mapbox distances from an origin,
 * recompute exact/surrounding/other + `recommended`, and return
 * { suburbs, distances } where distances maps suburb name -> km. Caps how many
 * suburbs get geocoded (by demand order) to keep API volume sane; the rest stay
 * as "other" with no distance.
 *
 * A suburb is only ever called "surrounding" on a distance we measured HERE.
 * The backend works its distances out from the coordinates on Collings listings,
 * and one bad listing coordinate is enough to drop a whole suburb in the wrong
 * place, which is how a Preston search came to pre-tick Sydney and Perth
 * (Christian, 2026-08-10). Mapbox geocodes each suburb inside a ~40km box around
 * the origin, so a suburb it can't find there simply isn't nearby, whatever the
 * backend said. Every lookup is cached in localStorage, so this costs one call
 * per suburb ever, and if the geocoder is unavailable we fall back to the
 * backend's numbers rather than un-ticking everything.
 */
export const enrichSuburbsWithDistance = async (rows, origin, radiusKm, state, cap = 60) => {
    const distances = {};
    if (!origin || !Array.isArray(rows) || !hasGeocoder()) {
        return { suburbs: rows, distances };
    }

    const head = rows.slice(0, cap);
    let resolved = 0;
    const geocoded = await Promise.all(head.map(async (s) => {
        // Flagged by the backend as belonging to another state: it cannot be a
        // neighbour, so don't spend a lookup on it.
        if (s.interstate) return { ...s, distance_km: null, measured: true };
        const c = await geocodeSuburb(s.suburb, state, origin);
        if (!c) return { ...s, measured: false };
        resolved += 1;
        const km = Math.round(haversineKm(origin.lat, origin.lng, c.lat, c.lng) * 10) / 10;
        return { ...s, distance_km: km, measured: true };
    }));
    const byName = new Map(geocoded.map((s) => [s.suburb, s]));
    // At least one suburb came back, so the geocoder is working and a miss is
    // real information ("not within 40km"), not an outage.
    const geocoderWorking = resolved > 0;

    const enriched = rows.map((s) => {
        const row = byName.get(s.suburb) || s;
        let distance = row.interstate ? null : row.distance_km;
        if (geocoderWorking && row.measured !== true) distance = null;

        let kind = 'other';
        let recommended = false;
        if (row.kind === 'exact') {
            kind = 'exact';
            recommended = true;
        } else if (distance != null && distance <= radiusKm) {
            kind = 'surrounding';
            recommended = true;
        }
        // The capital city (e.g. "Melbourne") is always pre-ticked as a broad-area
        // catch for interstate buyers who only marked the capital, regardless of
        // how far its centroid sits from the listing. Without this, the distance
        // recompute above silently un-ticks it (Melbourne CBD is well outside a
        // typical 5km radius), so it never auto-populates. The backend picks the
        // capital of the state being searched, so this can't tick another state's.
        if (row.is_capital) recommended = true;
        if (distance != null) distances[row.suburb] = distance;
        return { ...row, distance_km: distance, kind, recommended };
    });

    const kindOrder = { exact: 0, surrounding: 1, other: 2 };
    enriched.sort((a, b) => (
        (kindOrder[a.kind] - kindOrder[b.kind])
        || ((a.distance_km ?? 9999) - (b.distance_km ?? 9999))
        || (b.buyer_count - a.buyer_count)
        || a.suburb.localeCompare(b.suburb)
    ));

    return { suburbs: enriched, distances };
};
